import React from "react";
import { Add, InfoOutlined } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import WorkflowTaskRow from "./WorkflowTaskRow";
import {
  createWorkflowTask,
  WorkflowTask,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

interface WorkflowTaskPanelProps {
  tasks: WorkflowTask[];
  onChange: (tasks: WorkflowTask[]) => void;
}

const WorkflowTaskPanel: React.FC<WorkflowTaskPanelProps> = ({ tasks, onChange }) => {
  const viewOnly = useWorkflowReadOnly();
  const handleAdd = () => onChange([...tasks, createWorkflowTask()]);

  const handleChange = (id: string, patch: Partial<WorkflowTask>) => {
    onChange(tasks.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  };

  const handleRemove = (id: string) => {
    onChange(tasks.filter((task) => task.id !== id));
  };

  const handleMove = (id: string, direction: "up" | "down") => {
    const from = tasks.findIndex((task) => task.id === id);
    const to = direction === "up" ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= tasks.length) return;
    const reordered = [...tasks];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    onChange(reordered);
  };

  return (
    <Box className="workflow-canvas-dialog__task-panel">
      <Box className="workflow-canvas-dialog__task-intro">
        <InfoOutlined />
        <Typography>
          A Task canvas is a checklist. Add as many to-dos as needed and assign each one to a
          person.
        </Typography>
      </Box>

      {tasks.length === 0 ? (
        <Box className="workflow-canvas-dialog__empty">
          <Typography>No tasks yet — add the first one to get started.</Typography>
        </Box>
      ) : (
        tasks.map((task, index) => (
          <WorkflowTaskRow
            key={task.id}
            task={task}
            index={index}
            count={tasks.length}
            onChange={handleChange}
            onMove={handleMove}
            onRemove={handleRemove}
          />
        ))
      )}

      <Button
        disabled={viewOnly}
        className="workflow-canvas-dialog__mini-add"
        startIcon={<Add />}
        onClick={handleAdd}
      >
        Add task
      </Button>
    </Box>
  );
};

export default WorkflowTaskPanel;
